"use client";

import React, { FC, memo, useMemo } from "react";
import { cn } from "utils";
import { CategoryCardProps } from "./types";
import { AssetItem } from "./AssetItem";
import CheckBox from "@/src/components/CheckBox";

/**
 * CategoryCard 分类卡片组件
 * 显示一个分类的资产列表，支持全选/反选和单个资产选择
 *
 * @param props CategoryCardProps
 * @returns React.ReactElement
 */
export const CategoryCard: FC<CategoryCardProps> = memo(
  ({ title, category, assets, selectedIds, onSelectionChange, className }) => {
    /**
     * 限制显示的资产数量为最多4个（2行 x 2列）
     */
    const displayedAssets = useMemo(() => {
      return assets.slice(0, 4);
    }, [assets]);

    /**
     * 计算是否全选（仅针对显示的4个资产）
     */
    const isAllSelected = useMemo(() => {
      if (displayedAssets.length === 0) {
        return false;
      }
      return displayedAssets.every((asset) => selectedIds.includes(asset.id));
    }, [displayedAssets, selectedIds]);

    const hasAssets = displayedAssets.length > 0;
    const isIndeterminate =
      hasAssets && selectedIds.length > 0 && !isAllSelected;

    /**
     * 处理全选/反选点击（仅针对显示的4个资产）
     */
    const handleSelectAllClick = () => {
      const assetIds = displayedAssets.map((asset) => asset.id);
      const nextSelectedIds = isAllSelected ? [] : assetIds;
      onSelectionChange(category, assetIds[0] ?? "", nextSelectedIds);
    };

    /**
     * 处理单个资产选择变化
     */
    const handleItemSelectionChange = (
      assetId: string,
      isSelected: boolean,
    ) => {
      const newSelectedIds = isSelected
        ? Array.from(new Set([...selectedIds, assetId]))
        : selectedIds.filter((id) => id !== assetId);
      onSelectionChange(category, assetId, newSelectedIds);
    };

    return (
      <div
        className={cn(
          "flex-1 flex flex-col gap-2",
          "md:bg-background_white_overlay5 md:rounded-3 md:pl-4 md:pr-6 md:py-3",
          className,
        )}
      >
        {/* 标题区域 */}
        <div className="flex items-center gap-2 px-3 md:px-0">
          {/* 全选复选框 */}
          <CheckBox
            data-openreplay-label="WatchlistAddAssets · CategoryCard · CheckBox"
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onClick={handleSelectAllClick}
            classNames="w-4 h-4"
            iconSize={16}
          />
          <span className="body_medium_l text-content_primary">{title}</span>
        </div>

        {/* 桌面端分隔线 */}
        <div className="hidden md:block h-px bg-border_solid" />

        {/* 桌面端资产列表容器 - 网格布局：每行2个，最多2行（4个） */}
        <div className="hidden md:grid md:grid-cols-2">
          {displayedAssets.map((asset) => (
            <AssetItem
              key={asset.id}
              asset={asset}
              isSelected={selectedIds.includes(asset.id)}
              onSelectionChange={handleItemSelectionChange}
            />
          ))}
        </div>

        {/* H5 资产列表容器 - 垂直列表布局 */}
        <div className="md:hidden flex flex-col gap-1">
          {displayedAssets.map((asset) => (
            <div
              key={asset.id}
              className="bg-background_white_overlay5 rounded-2 px-3 py-1"
            >
              <AssetItem
                asset={asset}
                isSelected={selectedIds.includes(asset.id)}
                onSelectionChange={handleItemSelectionChange}
              />
            </div>
          ))}
        </div>
      </div>
    );
  },
);

CategoryCard.displayName = "CategoryCard";
